import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ClientService from "../../services/ClientService";
import { Toast } from "primereact/toast";
import RegisterUserService from "../../../Admin/services/RegisterUserService";
import ClientIntegrationPanel from "./ClientIntegrationPanel";
import AddSubscription from "../Subscription/AddSubscription";
import SubscriptionService from "../../services/SubscriptionService";
import {
  SaPageShell,
  SaPageHeader,
  SaTabs,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";
import { CLIENT_MODULES } from "../../../../shared/constants/clientModules";

function parseModuleFlags(raw) {
  const base = Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, true]));
  if (raw == null || raw === "") return base;
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return base;
    }
  }
  if (typeof o !== "object" || o === null) return base;
  CLIENT_MODULES.forEach((m) => {
    if (typeof o[m.key] === "boolean") base[m.key] = o[m.key];
  });
  return base;
}

const ROLES = [
  { id: 1, name: "SuperAdmin" },
  { id: 2, name: "Admin" },
  { id: 4, name: "Employee" },
];

function EditClient() {
  const toast = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    agencyName: "",
    email: "",
    phoneNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    gstNumber: "",
    agencyLogo: null,
    license: "",
    registrationNumber: "",
    leadCodePrefix: "",
    id,
  });

  const [formError, setFormError] = useState({});
  const [activeTab, setActiveTab] = useState("edit");
  const [loading, setLoading] = useState(true);
  const [moduleFlags, setModuleFlags] = useState(() =>
    Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, true]))
  );
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchClient = async () => {
      try {
        const data = await ClientService.getClientById(id);
        if (!cancelled) {
          const {
            moduleAccessJson,
            ModuleAccessJson,
            leadCodePrefix: lpCamel,
            LeadCodePrefix: lpPascal,
            ...profile
          } = data ?? {};
          const jsonRaw =
            moduleAccessJson !== undefined && moduleAccessJson !== null
              ? moduleAccessJson
              : ModuleAccessJson;
          const leadCodePrefix = lpCamel ?? lpPascal ?? "";
          setForm((prev) => ({ ...prev, ...profile, leadCodePrefix, id }));
          setModuleFlags(parseModuleFlags(jsonRaw));
        }
      } catch (error) {
        console.error("Error fetching client:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load client.",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchClient();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchSubscriptionStatus = async () => {
      try {
        const status = await SubscriptionService.getClientSubscriptionStatus(id);
        if (!cancelled) setSubscriptionStatus(status);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    };
    fetchSubscriptionStatus();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const list = await RegisterUserService.getUsersByClientId(id);
        if (!cancelled) setUsers(Array.isArray(list) ? list : list?.$values ?? []);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch users.",
          life: 4000,
        });
      }
    };
    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.agencyName.trim()) errors.agencyName = "Please enter the agency name.";
    setFormError(errors);
    if (Object.keys(errors).length > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please fix the highlighted fields.",
        life: 3500,
      });
    }
    return Object.keys(errors).length === 0;
  };

  const onClientSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    persistClient({ navigateAway: true });
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.keys(form).forEach((key) => {
      if (key === "moduleAccessJson" || key === "ModuleAccessJson") return;
      fd.append(key, form[key] ?? "");
    });
    fd.append("moduleAccessJson", JSON.stringify(moduleFlags));
    return fd;
  };

  const persistClient = async ({ navigateAway = false } = {}) => {
    try {
      await ClientService.updateClient(id, buildFormData());
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: navigateAway ? "Client updated successfully." : "Module access saved.",
        life: 2500,
      });
      if (navigateAway) setTimeout(() => navigate("/client"), 800);
    } catch (error) {
      console.error("Error updating client:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update client.",
        life: 4000,
      });
    }
  };

  const toggleModule = (key) => {
    setModuleFlags((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const agencyLogo = reader.result.split(",")[1];
      setForm((prev) => ({ ...prev, agencyLogo }));
      setFormError((prev) => ({ ...prev, agencyLogo: "" }));
    };
  };

  const deleteUser = async (userId) => {
    try {
      await RegisterUserService.deleteUser(userId);
      toast.current?.show({
        severity: "success",
        summary: "Removed",
        detail: "User deleted.",
        life: 2500,
      });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not delete user.",
        life: 3500,
      });
      console.error(e);
    }
  };

  const tabs = [
    { key: "edit", label: "Details", icon: "fas fa-id-card" },
    { key: "modules", label: "Modules", icon: "fas fa-toggle-on" },
    { key: "integration", label: "Integration", icon: "fas fa-plug" },
    { key: "users", label: "Users", icon: "fas fa-users" },
    { key: "services", label: "Services", icon: "fas fa-briefcase" },
    { key: "subscription", label: "Subscription", icon: "fas fa-receipt" },
  ];

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const enabledModuleCount = CLIENT_MODULES.filter((m) => moduleFlags[m.key]).length;

  const setAllModules = (enabled) => {
    setModuleFlags(Object.fromEntries(CLIENT_MODULES.map((m) => [m.key, enabled])));
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title={loading ? "Edit client" : form.agencyName || "Edit client"}
        subtitle={`Client ID ${id}. Switch tabs to manage integrations, users, and billing.`}
        actions={
          <>
            <SaGhostButton onClick={() => navigate("/client")}>
              <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
              All clients
            </SaGhostButton>
          </>
        }
      />

      <SaTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

      {subscriptionStatus && !subscriptionStatus.hasActiveSubscription ? (
        <div
          style={{
            margin: "16px 0 20px",
            padding: "14px 18px",
            borderRadius: SA.radiusMd,
            border: "1px solid #fde68a",
            background: "#fffbeb",
            color: "#92400e",
            fontSize: "13px",
            lineHeight: 1.55,
          }}
        >
          <strong style={{ display: "block", marginBottom: "4px" }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: "8px" }} />
            No active subscription
          </strong>
          This client has{" "}
          {subscriptionStatus.totalCount > 0
            ? `${subscriptionStatus.totalCount} past subscription(s), but none are active today.`
            : "no subscription on record."}{" "}
          Tenant users cannot access modules or API until you add an active subscription under the{" "}
          <strong>Subscription</strong> tab.
        </div>
      ) : null}

      {subscriptionStatus?.hasActiveSubscription ? (
        <div
          style={{
            margin: "16px 0 20px",
            padding: "12px 18px",
            borderRadius: SA.radiusMd,
            border: "1px solid #bbf7d0",
            background: "#f0fdf4",
            color: "#166534",
            fontSize: "13px",
          }}
        >
          <i className="fas fa-check-circle" style={{ marginRight: "8px" }} />
          Active subscription:{" "}
          <strong>
            {subscriptionStatus.activeSubscription?.subscriptionPlan?.subscriptionPlanName ?? "Plan"}
          </strong>
          {" · "}
          until{" "}
          {subscriptionStatus.activeSubscription?.endDate
            ? new Date(subscriptionStatus.activeSubscription.endDate).toLocaleDateString()
            : "—"}
        </div>
      ) : null}

      {activeTab === "edit" && (
        <form onSubmit={onClientSubmit}>
          <SaCard>
            <SaSectionTitle iconClass="fas fa-building">Agency profile</SaSectionTitle>
            <div style={grid}>
              <SaField label="Agency name *">
                <input
                  type="text"
                  name="agencyName"
                  value={form.agencyName}
                  onChange={changeHandler}
                  style={{
                    ...saInputSx,
                    borderColor: formError.agencyName ? SA.danger : SA.border,
                  }}
                />
                {formError.agencyName ? (
                  <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError.agencyName}</p>
                ) : null}
              </SaField>
              <SaField label="Email">
                <input type="email" name="email" value={form.email} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Phone">
                <input type="text" name="phoneNumber" value={form.phoneNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Street address">
                <input type="text" name="streetAddress" value={form.streetAddress} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="City">
                <input type="text" name="city" value={form.city} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="State">
                <input type="text" name="state" value={form.state} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Postal code">
                <input type="text" name="postalCode" value={form.postalCode} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Country">
                <input type="text" name="country" value={form.country} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="GST number">
                <input type="text" name="gstNumber" value={form.gstNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Agency logo">
                <input type="file" accept="image/*" onChange={onFileChange} style={{ fontSize: "13px", color: SA.muted }} />
              </SaField>
              <SaField label="License">
                <input type="text" name="license" value={form.license} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Registration number">
                <input type="text" name="registrationNumber" value={form.registrationNumber} onChange={changeHandler} style={saInputSx} />
              </SaField>
              <SaField label="Lead code prefix" hint="Optional. Alphanumeric; leads use PREFIX-DT5W-id. Blank = DT5W-id only.">
                <input
                  type="text"
                  name="leadCodePrefix"
                  value={form.leadCodePrefix}
                  onChange={changeHandler}
                  placeholder="e.g. ACME"
                  maxLength={32}
                  style={saInputSx}
                />
              </SaField>
            </div>
            <SaFormActions>
              <SaPrimaryButton type="submit" iconClass="fas fa-save">
                Save changes
              </SaPrimaryButton>
            </SaFormActions>
          </SaCard>
        </form>
      )}

      {activeTab === "modules" && (
        <SaCard>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "22px",
            }}
          >
            <div style={{ flex: "1 1 320px", maxWidth: "640px" }}>
              <SaSectionTitle iconClass="fas fa-toggle-on">Module access</SaSectionTitle>
              <p style={{ margin: 0, fontSize: "14px", color: SA.muted, lineHeight: 1.6 }}>
                Turn features on or off for this tenant. Disabled modules are hidden from menus and blocked by URL for
                Admin and Employee users. Super Admin is unaffected. An active subscription is required before any
                module access is granted to tenant users.
              </p>
              {subscriptionStatus && !subscriptionStatus.hasActiveSubscription ? (
                <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#b45309", lineHeight: 1.5 }}>
                  Module toggles below are saved for when a subscription is added, but tenant users currently have no
                  effective access.
                </p>
              ) : null}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  padding: "10px 16px",
                  background: SA.bgSoft,
                  borderRadius: SA.radiusMd,
                  border: `1px solid ${SA.border}`,
                  fontSize: "13px",
                  fontWeight: "700",
                  color: SA.ink,
                  whiteSpace: "nowrap",
                }}
              >
                {enabledModuleCount} of {CLIENT_MODULES.length} enabled
              </div>
              <SaGhostButton type="button" onClick={() => setAllModules(true)}>
                Enable all
              </SaGhostButton>
              <SaGhostButton type="button" onClick={() => setAllModules(false)}>
                Disable all
              </SaGhostButton>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "16px",
            }}
          >
            {CLIENT_MODULES.map((m) => {
              const enabled = !!moduleFlags[m.key];
              return (
                <div
                  key={m.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleModule(m.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleModule(m.key);
                    }
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    padding: "16px",
                    minHeight: "148px",
                    border: `1.5px solid ${enabled ? "rgba(94,114,228,0.35)" : SA.border}`,
                    borderRadius: "14px",
                    background: enabled
                      ? "linear-gradient(135deg, #fafbff 0%, #f8f9fe 100%)"
                      : "#fff",
                    cursor: "pointer",
                    boxShadow: enabled ? "0 4px 16px rgba(94,114,228,0.1)" : "none",
                    transition: "border-color .2s, box-shadow .2s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "12px",
                        background: enabled ? SA.gradientBtn : SA.bgSoft,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={m.icon || "fas fa-cube"}
                        style={{ color: enabled ? "#fff" : SA.muted, fontSize: "16px" }}
                      />
                    </div>
                    <div
                      aria-hidden
                      style={{
                        width: "42px",
                        height: "24px",
                        borderRadius: "12px",
                        background: enabled ? SA.primary : "#d1d5db",
                        position: "relative",
                        flexShrink: 0,
                        transition: "background .2s",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "3px",
                          left: enabled ? "21px" : "3px",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          background: "#fff",
                          transition: "left .2s",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "800", fontSize: "14px", color: SA.ink, lineHeight: 1.3 }}>{m.label}</div>
                    <div style={{ marginTop: "6px", fontSize: "12px", color: SA.muted, lineHeight: 1.55 }}>
                      {m.description}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      letterSpacing: "0.02em",
                      color: enabled ? SA.success : SA.muted,
                    }}
                  >
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              );
            })}
          </div>

          <SaFormActions>
            <SaPrimaryButton type="button" iconClass="fas fa-save" onClick={() => persistClient({ navigateAway: false })}>
              Save module access
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      )}

      {activeTab === "integration" && <ClientIntegrationPanel clientId={id} />}

      {activeTab === "users" && (
        <SaCard noPadding>
          <div style={{ padding: "20px 24px", display: "flex", justifyContent: "flex-end", borderBottom: `1px solid ${SA.border}` }}>
            <SaPrimaryButton
              iconClass="fas fa-user-plus"
              onClick={() => navigate("/addclientuser", { state: { clientId: id } })}
            >
              Add user
            </SaPrimaryButton>
          </div>
          {users.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: SA.muted }}>
              No users linked to this client yet.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table mb-0" style={{ width: "100%" }}>
                <thead style={{ background: SA.bgSoft }}>
                  <tr>
                    {["Name", "Email", "Phone", "Address", "Role", "Status", "Actions"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          fontSize: "11px",
                          fontWeight: "800",
                          color: SA.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleName = ROLES.find((r) => r.id == user.roleId)?.name ?? "—";
                    return (
                      <tr key={user.id}>
                        <td style={{ padding: "12px 16px", fontSize: "13px", fontWeight: "600", color: SA.ink }}>
                          {user.firstName} {user.lastName}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{user.email}</td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{user.contactNumber}</td>
                        <td style={{ padding: "12px 16px", fontSize: "12px", color: SA.muted, maxWidth: "220px" }}>
                          {`${user.streetAddress || ""}, ${user.city || ""}, ${user.state || ""}`}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{roleName}</td>
                        <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: "700",
                              background: user.isActive ? "#e8f5e9" : "#fdecea",
                              color: user.isActive ? SA.success : SA.danger,
                            }}
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button
                            type="button"
                            title="Edit user"
                            onClick={() => navigate(`/editclientuser/${user.id}`)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              marginRight: "8px",
                              cursor: "pointer",
                              color: SA.primary,
                            }}
                          >
                            <i className="fas fa-pen" />
                          </button>
                          <button
                            type="button"
                            title="Delete user"
                            onClick={() => deleteUser(user.id)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              cursor: "pointer",
                              color: SA.danger,
                            }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SaCard>
      )}

      {activeTab === "services" && (
        <SaCard>
          <SaSectionTitle iconClass="fas fa-briefcase">Services</SaSectionTitle>
          <p style={{ margin: 0, fontSize: "14px", color: SA.muted, lineHeight: 1.6 }}>
            Additional managed services for this client can be listed here. Hook this tab to your billing or operations module when ready.
          </p>
        </SaCard>
      )}

      {activeTab === "subscription" && (
        <div style={{ marginTop: "4px" }}>
          <AddSubscription
            activeTab={activeTab}
            ClientId={id}
            onSubscriptionsChanged={async () => {
              try {
                const status = await SubscriptionService.getClientSubscriptionStatus(id);
                setSubscriptionStatus(status);
              } catch (error) {
                console.error("Error refreshing subscription status:", error);
              }
            }}
          />
        </div>
      )}
    </SaPageShell>
  );
}

export default EditClient;
